/**
 * SKY CLOCK — pure day/night time model + sun/moon orbit geometry.
 * `hour` value that grows without bound across a run:
 *
 *   - One cycle = 24 hours. hour 24 → next cycle's hour 0, hour 48 → cycle 2, ...
 *   - phase 0..12  = DAYTIME  → the SUN travels its arc.
 *   - phase 12..24 = NIGHTTIME → the MOON travels the same arc (opposite body).
 *
 * Sun/moon share ONE elliptical orbit (Minecraft-style). A body's local progress
 * p (0..1) walks the upper half of the ellipse:
 *   p=0   → east summit  (leftmost point, BELOW the horizon — hidden)
 *   p=0.5 → apex         (top of the arc, well above the horizon)
 *   p=1   → west summit  (rightmost point, BELOW the horizon — hidden)
 *
 * The sun rises in the east (left) at phase 0, peaks at phase 6, sets in the west
 * (right) at phase 12. The moon does the same across phase 12..24, so as the sun
 * sets west the moon rises east, and vice-versa.
 */

import {
  CELESTIAL_DEFAULT_BODY_RADIUS_PX,
  computeSummitBelowOffset,
  isCelestialDiscVisible,
} from "./day-night.js";
import { resolveSkyAnchorColumns } from "../layout/responsive-board.js";

export const CYCLE_HOURS = 24;
export const DAY_HOURS = 12;

/** Sea horizon as fraction of the core/grid box height from its top (mockup: 95%). */
export const SEA_LINE_RATIO = 0.95;

export function clamp01(v) {
  if (v <= 0) return 0;
  if (v >= 1) return 1;
  return v;
}

export function easeInOut(t) {
  t = clamp01(t);
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * Split a continuous hour into cycle index, in-cycle phase, and the active body.
 * @param {number} hour continuous, >= 0
 */
export function decomposeHour(hour) {
  var h = Number(hour);
  if (!isFinite(h) || h < 0) h = 0;
  var cycleIndex = Math.floor(h / CYCLE_HOURS);
  var phase = h - cycleIndex * CYCLE_HOURS; // 0..24
  var isDay = phase < DAY_HOURS;
  var bodyProgress = isDay
    ? phase / DAY_HOURS
    : (phase - DAY_HOURS) / DAY_HOURS;
  return {
    hour: h,
    cycleIndex: cycleIndex,
    phase: phase,
    isDay: isDay,
    bodyKind: isDay ? "sun" : "moon",
    bodyProgress: clamp01(bodyProgress),
  };
}

/**
 * Continuous hour → normalized color phase (0..1) for the existing sky/sea
 * keyframe ramps (0 = dawn warm, 0.25 = day blue, 0.5 = sunset warm,
 * 0.75 = deep night, 1 = dawn warm again).
 *
 * Shaped so the day holds bright blue through its middle with only brief warm
 * ramps near the edges, and the night holds deep blue likewise. The warm ramps
 * intentionally spill a little past the horizon crossings (phase 0/12/24) so the
 * sky still glows just after sunset and just before sunrise, while the sun/moon
 * disc itself is already hidden below the horizon.
 * @param {number} hour
 */
export function hourToColorPhase(hour) {
  var d = decomposeHour(hour);
  var phase = d.phase;
  if (phase < 2) return lerp(0.0, 0.25, phase / 2); // sunrise: dawn warm → day blue
  if (phase <= 10) return 0.25; // day blue plateau
  if (phase < 12) return lerp(0.25, 0.5, (phase - 10) / 2); // → sunset warm
  if (phase < 14) return lerp(0.5, 0.75, (phase - 12) / 2); // dusk glow → night
  if (phase <= 22) return 0.75; // deep night plateau
  return lerp(0.75, 1.0, (phase - 22) / 2); // pre-dawn glow
}

function lerp(a, b, u) {
  return a + (b - a) * u;
}

/**
 * Vertical (phone) layout: the apex sits this many body radii below the header band, so the
 * body's face clears the header bar instead of hiding behind it for most of the Fall.
 */
export const VERTICAL_APEX_CLEARANCE = 1.15;

/**
 * Orbit anchors from the game layout strips: drawing geometry only (nothing in the game
 * logic reads it). The east/west summits sit just INSIDE the playfield column (one body
 * radius in from its edges) and BELOW the sea horizon, so the body is fully hidden there
 * and rises / sets in the open: outside the column the HUD side panels (evolution ring,
 * stash card) would cover the sunset and the moon rising out of the sea. Higher up the arc
 * widens to `wideHalfWidth` (half the span between points just OUTSIDE the side columns, the
 * sweep it always had), so on wide layouts mid-morning and the Merge moon (hours 16–21) sit
 * close to where they did, clear of the jar; on phones the sweep is capped to keep the disc on
 * screen. The apex is centred over the play area: just above it on wide layouts, just below
 * the header band on phones. The hour → progress mapping is untouched.
 * @param {{
 *   coreRect?: { x: number, y: number, width: number, height: number },
 *   playArea?: { x: number, y: number, width: number, height: number },
 *   leftStrip?: { x: number, width: number },
 *   rightStrip?: { x: number, width: number },
 *   gridUnitPx?: number,
 *   gridWidthPx?: number,
 * }} layout
 */
export function computeOrbitAnchors(layout, opts) {
  layout = layout || {};
  opts = opts || {};
  var sky = resolveSkyAnchorColumns(layout);
  var core = sky.coreRect;
  var play = sky.playArea;
  var left = sky.leftStrip;
  var right = sky.rightStrip;

  var seaLineY;
  if (layout.layoutMode === "vertical" && play && isFinite(play.y) && isFinite(play.height)) {
    seaLineY = play.y + play.height * SEA_LINE_RATIO;
  } else {
    seaLineY = core.y + core.height * SEA_LINE_RATIO;
  }
  var belowOffset = computeSummitBelowOffset(core, opts.bodyRadius);
  var r = Number(opts.bodyRadius) > 0 ? Number(opts.bodyRadius) : CELESTIAL_DEFAULT_BODY_RADIUS_PX;

  // One radius in from the playfield edges (never past a quarter of its width).
  var edgeIn = Math.min(r, play.width * 0.25);
  var eastX = play.x + edgeIn;
  var westX = play.x + play.width - edgeIn;
  var outMargin = Math.max(40, core.width * 0.06);
  var narrowHalf = (westX - eastX) * 0.5;
  var wideHalfWidth = ((right.x + right.width + outMargin) - (left.x - outMargin)) * 0.5;
  if (layout.layoutMode === "vertical") {
    // On a phone the play band is the whole screen: keep the disc on it (the layout's own
    // side strips are the scenery margins out to the screen edges).
    var midX = play.x + play.width * 0.5;
    var screenL = layout.leftStrip && isFinite(layout.leftStrip.x) ? layout.leftStrip.x : play.x;
    var screenR = layout.rightStrip && isFinite(layout.rightStrip.x)
      ? layout.rightStrip.x + (Number(layout.rightStrip.width) || 0)
      : play.x + play.width;
    wideHalfWidth = Math.min(wideHalfWidth, Math.min(midX - screenL, screenR - midX) - r);
  }
  wideHalfWidth = Math.max(narrowHalf, wideHalfWidth);
  var summitY = seaLineY + belowOffset;
  var apexY = play.y - Math.max(8, play.height * 0.02);
  if (layout.layoutMode === "vertical") {
    // The vertical header band ends where the play band starts.
    apexY = Math.min(play.y + r * VERTICAL_APEX_CLEARANCE, seaLineY - r * 2);
  }
  var apex = {
    x: play.x + play.width * 0.5,
    y: apexY,
  };

  return {
    east: { x: eastX, y: summitY },
    west: { x: westX, y: summitY },
    apex: apex,
    summitY: summitY,
    seaLineY: seaLineY,
    wideHalfWidth: wideHalfWidth,
  };
}

/**
 * Position of a body on the shared orbit: an upper half-ellipse whose half-width grows with
 * height, from the summits' (low, inside the playfield) to `anchors.wideHalfWidth` (high), so
 * the arc is a dome that is wide in the sky and narrow at the sea. Heights follow the plain
 * ellipse, so how fast the body clears the sea at a given hour is unchanged.
 * @param {ReturnType<typeof computeOrbitAnchors>} anchors
 * @param {number} progress 0..1 (0 = east summit, 0.5 = apex, 1 = west summit)
 * @param {{ bodyRadius?: number }} [opts]
 */
export function computeOrbitPosition(anchors, progress, opts) {
  opts = opts || {};
  var p = clamp01(progress);
  var cx = (anchors.east.x + anchors.west.x) * 0.5;
  var a = (anchors.west.x - anchors.east.x) * 0.5;
  var cy = anchors.summitY;
  var b = Math.max(1, cy - anchors.apex.y);

  var theta = Math.PI * (1 - p); // p=0 → east (left), p=1 → west (right)
  var sinT = Math.sin(theta);
  var wide = anchors.wideHalfWidth > a ? anchors.wideHalfWidth : a;
  var x = cx + (a + (wide - a) * sinT) * Math.cos(theta);
  var y = cy - b * sinT;

  var bodyRadius = Math.max(0, Number(opts.bodyRadius) || 0);
  var aboveHorizon = isCelestialDiscVisible(y, anchors.seaLineY, bodyRadius, p);

  return { x: x, y: y, aboveHorizon: aboveHorizon, progress: p, theta: theta };
}

/**
 * Convenience: full celestial state for a given hour + layout.
 * @param {number} hour
 * @param {object} layout
 */
export function computeCelestialState(hour, layout, opts) {
  opts = opts || {};
  var d = decomposeHour(hour);
  var bodyRadius = Math.max(0, Number(opts.bodyRadius) || 0);
  var anchors = computeOrbitAnchors(layout, { bodyRadius: bodyRadius });
  var pos = computeOrbitPosition(anchors, d.bodyProgress, { bodyRadius: bodyRadius });
  return {
    hour: d.hour,
    cycleIndex: d.cycleIndex,
    phase: d.phase,
    isDay: d.isDay,
    bodyKind: d.bodyKind,
    bodyProgress: d.bodyProgress,
    colorPhase: hourToColorPhase(hour),
    anchors: anchors,
    position: pos,
  };
}
